import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormArray, AbstractControl, FormControl, ValidatorFn } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { GoalButtonActions, GoalsModel } from 'src/app/models/goals.model';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-goal-form',
  templateUrl: './goal-form.component.html',
  styleUrls: ['./goal-form.component.scss']
})
export class GoalFormComponent implements OnInit {

  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<any>();
  @Input() action: 'confirm' | 'update' | 'delete' = 'confirm';
  @Input() existingData: GoalsModel;
  minDate: Date;
  goalButtonActions = GoalButtonActions;

  constructor(
    private _fb: FormBuilder,
    private _datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    switch(this.action) {
      case this.goalButtonActions.UPDATE:
        this.updateFormWithExisitingData();
      break;
      case this.goalButtonActions.DELETE:
        this.updateFormWithExisitingData();
        this.newTaskName.disable();
        this.newTaskDueDate.disable();
        this.goals_form.get('goal').disable();
      break;
    }
    this.minDate = new Date();
  }

  goals_form = this._fb.group({
    goal: this._fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      due: ['', [Validators.required, this.minDateValidator()]],
      description: ['', [Validators.maxLength(100)]],
    }),
    tasks: this._fb.array([], this.minLengthArray(1)),
  });

  newTaskName = new FormControl('', [Validators.required, Validators.maxLength(50)]);
  newTaskDueDate = new FormControl(null, [Validators.required]);  

  get formTasks(): FormArray {
    return this.goals_form.get('tasks') as FormArray;
  }

  getMinDate(): Date {
    return this.minDate;
  }

  minDateValidator() {
    return (control: FormControl): {[key: string]: any} | null => {
      const selectedDate = new Date(control.value);
      if (selectedDate < this.minDate) {
        return { 'minDate': true };
      }
      return null;
    };
  }

  dateFilter = (d: Date): boolean => {
    const value = this.goals_form.value;
    return (d >= this.minDate) && (d <= this.toDate(value.goal.due))
  }

  isFormGroup(control: AbstractControl): control is FormGroup {
    return control instanceof FormGroup;
  }

  updateFormWithExisitingData() {
    const formatDueDate = this.parseDateString(this.existingData.due);

    this.goals_form.get('goal').patchValue({
        name: this.existingData.name,
        due: formatDueDate.toISOString(),
        description: this.existingData.description,
      })
      this.formTasks.clear();
      this.existingData.goalTasks.forEach(task => {
        const taskGroup = this._fb.group({
          isDone: task.isDone,
          name: task.name,
          due: task.due
        });

        this.formTasks.push(taskGroup);
      })
  }

  parseDateString(dateString: string): Date {
    const [month, day, year] = dateString.split('/');
    const fullYear = +year < 100 ? +year + 2000 : +year;
    return new Date(fullYear, +month - 1, +day, 12, 0, 0);
  }

  onAddTask(event: Event) {
    if(this.newTaskName.valid && this.newTaskDueDate.valid) {

      let formattedDate = this._datePipe.transform(this.newTaskDueDate.value, 'shortDate')
      
      const newTask = this._fb.group({
        name: this.newTaskName.value,
        due: formattedDate,
      });

      this.formTasks.push(newTask);

      this.newTaskName.reset();
      this.newTaskDueDate.reset();
    }
  }

  onDeleteTask(id: any) {
    this.formTasks.removeAt(id);
  }

  onConfirmClick() {
    if(this.goals_form.valid) {
      this.formSubmit.emit(this.goals_form.value);
    }
  }

  onCancelClick() {
    this.formCancel.emit();
  }

  createGoal() {
    if(this.goals_form.valid) {
      this.formSubmit.emit(this.goals_form.value);
    }
  }

  updateGoal() {
    if(this.goals_form.valid) {
      this.formSubmit.emit(this.goals_form.value);
    }
  }

  deleteGoal() {
    if(this.goals_form.valid) {
      this.formSubmit.emit(true);
    }
  }

  get buttonAction() {
    switch(this.action) {
      case this.goalButtonActions.CONFIRM:
         return this.createGoal();
      case this.goalButtonActions.UPDATE:
        return this.updateGoal();
      case this.goalButtonActions.DELETE:
        return this.deleteGoal();
      default:
        return this.createGoal();
    }
  }

  get buttonText(): string {
    switch(this.action) {
      case this.goalButtonActions.CONFIRM:
        return 'Confirm';
      case this.goalButtonActions.UPDATE:
        return 'Update';
      case this.goalButtonActions.DELETE:
        return 'Delete';
      default:
        return 'Confirm';
    }
  }

  get isFormValid(): boolean {
    return this.goals_form.valid && this.goals_form.get('tasks').valid;
  }

  minLengthArray(min: number): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (control instanceof FormArray) {
        return control.length >= min ? null : { minLengthArray: {valid: false} };
      }
      return null;
    };
  }
  protected toDate(d: Date | string): Date {
    return (typeof d === 'string') ? new Date(d) : d;
  }
}
